import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { clearLocalFormDrafts } from '../hooks/useFormDrafts';
import { shouldClearPreviousUserData } from '../lib/auth/sharedDeviceGuard';
import { isPostAuthFailure } from '../lib/auth/postAuthFailure';
import { isRedirectStorageTeardownError } from '../lib/auth/redirectTeardownError';
import { isTransientAuthError } from '../lib/auth/transientAuthError';
import { isExpectedAuthError } from '../lib/auth/expectedAuthError';
import { onAuthStateChange, signIn as firebaseSignIn, signInWithGoogle as firebaseSignInWithGoogle, signOut as firebaseSignOut, resetPassword as firebaseResetPassword, updateUserProfileData, completePendingRedirect, clearOfflineCache } from '../lib/firebase-auth';
import { clearSessionFilters } from '../lib/support/inboxFilterSession';
import { clearHeldCreates } from '../lib/fieldService/heldCreates';
import { getUserProfile } from '../lib/firebase-helpers';
import { isConnectivityFailure, loadSavedIdentity, saveIdentity } from '../lib/auth/offlineIdentityCache';
import { setUserContext as setErrorLoggingUserContext } from '../lib/datahub/errorLogging/captureError';
import { analytics } from '../lib/analytics';
import { resetScopedSearchKeyCache, prewarmSearchKeys } from '../lib/search/typesenseScopedSearch';
import type { User, UserRole } from '../types/firestore';
import toast from 'react-hot-toast';

// localStorage key holding the uid of the last user authenticated ON THIS DEVICE.
// Used to detect a user SWITCH between page loads so the prior user's on-disk
// Firestore cache can be wiped before the new user reads anything (shared-tablet
// data-at-rest guard — see the onAuthStateChange handler and logout()).
const LAST_AUTH_UID_KEY = 'oh_last_auth_uid';



// ============================================================================
// TYPES
// ============================================================================

interface AuthContextType {
  // Authentication state
  firebaseUser: FirebaseUser | null;
  user: User | null;
  loading: boolean;
  /**
   * True while `user` is the profile SAVED ON THIS DEVICE, because the live
   * read failed for lack of signal (see lib/auth/offlineIdentityCache.ts).
   * Only ever true in an app that registered an offline identity store.
   */
  profileFromDevice: boolean;
  /**
   * The Firebase uid `user` was fetched FOR. During a direct account switch
   * `firebaseUser` changes before the new profile loads, so for a moment `user`
   * is still the previous account's profile. A consumer that pairs the uid with
   * a profile field (e.g. the tech app's offline save rule) must check
   * `profileUid === firebaseUser?.uid` first. Null whenever `user` is null.
   */
  profileUid: string | null;

  // Authentication methods
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;

  // User profile methods
  updateProfile: (updates: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;

  // Utility methods
  hasRole: (role: UserRole) => boolean;
  isAdmin: () => boolean;
  isManagerOrAdmin: () => boolean;
  isInvestor: () => boolean;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileFromDevice, setProfileFromDevice] = useState(false);
  const [profileUid, setProfileUid] = useState<string | null>(null);

  // Complete any pending OAuth redirect. Runs once on mount, UNCONDITIONALLY —
  // getRedirectResult() is a harmless no-op when no redirect is in flight, and
  // it MUST run on every platform. signInWithGoogle() falls back to
  // signInWithRedirect whenever the popup is blocked (common on mobile-Chrome
  // tabs and Android, not just iOS), so gating this on shouldUseRedirectAuth()
  // (iOS-only) left the returning credential unconsumed on those platforms and
  // bounced the user straight back to the login page. onAuthStateChange picks
  // up the signed-in user once the credential exchange finishes. Errors are
  // swallowed inside completePendingRedirect (e.g. iOS Safari storage
  // partitioning) so the app still renders.
  useEffect(() => {
    completePendingRedirect().catch((error) => {
      if (error?.code === 'auth/user-disabled') {
        console.warn('Google sign-in (redirect): account disabled:', error.message);
        toast.error('This account has been disabled. Please contact your administrator.');
      } else if (error?.message?.includes('No account found')) {
        console.warn('Google sign-in (redirect): no account found:', error.message);
        toast.error('No account found — please contact your administrator for an invitation.');
      } else if (isTransientAuthError(error)) {
        // Network dropped / Google's backend answered 5xx while finishing the
        // redirect. Nothing on our side to fix — warn (not error) so the
        // console bridge doesn't report it, and tell the user to retry.
        console.warn('Google sign-in (redirect): transient network/backend failure:', error?.message);
        toast.error("Couldn't reach Google sign-in. Check your connection and try again.");
      } else if (isRedirectStorageTeardownError(error)) {
        // The tab was backgrounded or closing while getRedirectResult() ran, so
        // the SDK's IndexedDB handle went away under it. There is no user on the
        // other side to tell, and nothing is broken — warn (not error) so the
        // console bridge stops filing normal browser behaviour as a defect.
        // 12 of the 18 observed events on this branch are this shape. GH #3486.
        console.warn('Google sign-in (redirect): storage torn down mid-completion:', error?.message);
      } else if (isPostAuthFailure(error)) {
        // Sign-in SUCCEEDED; something after it failed. completePendingRedirect
        // calls ensureUserProfileExists AFTER getRedirectResult resolves and
        // tags anything thrown there (see firebase-auth.ts), so a transient
        // Firestore blip in that profile read arrives here already marked.
        // Telling them "sign-in could not be completed" would be a false alarm —
        // onAuthStateChange does its OWN profile read a moment later, and if
        // that one succeeds they land on the dashboard having seen nothing
        // wrong. If it ALSO fails, the stranding sink in the onAuthStateChange
        // catch below tells them. This branch is silent because that one
        // speaks — NOT because the failure is assumed to heal.
        //
        // It is not retried, either: the `if (!profile)` retry there only
        // re-reads a MISSING profile, never a read that threw. An earlier
        // version of this comment claimed a retry that does not exist.
        //
        // Deliberately the TAG and not `auth.currentUser`: ambient state answers
        // "is anyone signed in", which is also true for a user who is already
        // signed in and starts a NEW redirect that genuinely fails — and that
        // person must still be told.
        console.error('Pending redirect completion failed after sign-in succeeded:', error);
      } else {
        // A REAL failed sign-in on the redirect path — which is the ONLY path on
        // iOS/iPadOS (Safari blocks popups outright; see shouldUseRedirectAuth).
        // This branch used to log and show nothing, returning the person to
        // /login with no explanation at all: 18 observed events across
        // OFFICEHUB-CLIENT-2S and -47, 6 of them unambiguously mid-login (5x the
        // blocking-function deadline, 1x network-request-failed). The popup path
        // toasts on every one of its branches; this half of the same journey did
        // not. Keep the error log AND tell the person.
        console.error('Pending redirect completion failed:', error);
        toast.error('Sign-in could not be completed. Please try again.');
      }
    });
  }, []);

  // Subscribe to authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (authUser) => {
      // Any auth-state transition (login, logout, user switch) must invalidate
      // the cached per-org Typesense scoped search key — it embeds the previous
      // user's org and would otherwise keep scoping searches to it in the same
      // SPA session. See client/src/lib/search/typesenseScopedSearch.ts.
      resetScopedSearchKeyCache();
      setFirebaseUser(authUser);

      if (authUser) {
        // Shared-device guard (offline cache): if a DIFFERENT user than the last
        // one on this device is signing in, the prior user's Firestore IndexedDB
        // cache is still on disk. Wipe it BEFORE this session reads anything,
        // then hard-reload into a fresh empty cache. This covers the dominant
        // shared-field-tablet path that the explicit logout() clear misses: Rep
        // A's token expires while the tablet sits idle -> app drops to /login
        // (no logout tapped) -> Rep B signs in. We only act on a real user
        // CHANGE (not the transient initial null, not a same-user token
        // refresh), so a normal reload never wipes a user's own cache.
        try {
          const lastUid = window.localStorage.getItem(LAST_AUTH_UID_KEY);
          if (shouldClearPreviousUserData(lastUid, authUser.uid)) {
            window.localStorage.setItem(LAST_AUTH_UID_KEY, authUser.uid);
            // Locally-autosaved form drafts are keyed by templateId alone and survive
            // sign-out, so without this the previous technician's in-progress content
            // (customer, site, readings, preview text) sits in the draft picker for the
            // next person who logs in on this tablet. Same shared-device transition the
            // Firestore cache wipe below already covers; these keys were missed. The same
            // wipe also clears quote-wizard drafts (#3924), which are org- or quote-keyed.
            // Deliberately NOT cleared on a same-user logout/login - local drafts have
            // no Firestore copy, so that would destroy the user's own resumable work.
            clearLocalFormDrafts();
            clearHeldCreates();
            await clearOfflineCache();
            window.location.reload();
            return;
          }
          window.localStorage.setItem(LAST_AUTH_UID_KEY, authUser.uid);
        } catch {
          // localStorage unavailable (private mode) — skip the guard rather than
          // block sign-in. Worst case falls back to the explicit-logout clear.
        }

        // Fetch user profile from Firestore. Retry once after a short delay
        // to handle the race condition where Auth completes before the
        // Firestore user document is written (e.g. acceptInvitation).
        try {
          // Ensure the ID token is fresh before any Firestore call.
          // onAuthStateChanged can fire with a cached user whose token is
          // expired (>1 hr idle). The Firestore SDK retries internally, but
          // its async-queue produces an uncaught rejection on the first
          // failed attempt. Forcing a refresh here avoids that entirely.
          await authUser.getIdToken();

          let profile = await getUserProfile(authUser.uid);
          if (!profile) {
            // Wait briefly — the profile doc may still be writing
            await new Promise((r) => setTimeout(r, 2000));
            profile = await getUserProfile(authUser.uid);
          }
          if (profile) {
            setUser(profile);
            setProfileUid(authUser.uid);
            setProfileFromDevice(false);
            // Offline cold start (plan step A): keep this device's copy current.
            // A no-op unless the app registered an offline identity store.
            saveIdentity(authUser.uid, { user: profile });
            // Mint the Typesense scoped search keys now rather than on the first
            // keystroke. The mint is a Cloud Function round trip that would
            // otherwise sit on the critical path of the user's first search,
            // cold start included. Fire-and-forget — search falls back correctly
            // if it fails. Must stay AFTER resetScopedSearchKeyCache() above, or
            // it would warm the cache the reset is meant to clear.
            prewarmSearchKeys();
            // Attach user context to the error logger so error_events
            // include userId/userEmail. Previously this was never wired up,
            // leaving every error in error_events with user: null and
            // making it impossible to tell which rep a given issue affected.
            setErrorLoggingUserContext(profile.userId, profile.email);
            analytics.identify(profile.userId, {
              email: profile.email,
              role: profile.role ?? '',
              organizationId: profile.organizationId ?? '',
            });
          } else {
            console.warn('User profile not found in Firestore after retry — signing out');
            await firebaseSignOut();
            setUser(null);
            setProfileUid(null);
            setErrorLoggingUserContext(undefined, undefined);
          }
        } catch (error) {
          // OFFLINE COLD START (plan step A). No signal — the token refresh or
          // the profile read could not reach the server — and this device holds
          // the profile THIS uid saved on a previous online start: render from it
          // (the persisted query cache has the tickets) instead of stranding the
          // technician on "No workspace assigned". Connectivity failures only: a
          // server refusal (permission-denied — a deactivated account) never
          // falls back. The real read is retried when signal returns (effect
          // below). No store registered (every app but the technician PWA) =>
          // loadSavedIdentity is null and the stranding path below runs as before.
          const saved = isConnectivityFailure(error) ? await loadSavedIdentity(authUser.uid) : null;
          if (saved) {
            console.warn('Profile read failed offline — using the profile saved on this device.');
            setUser(saved.user);
            setProfileUid(authUser.uid);
            setProfileFromDevice(true);
            setErrorLoggingUserContext(saved.user.userId, saved.user.email);
            setLoading(false);
            return;
          }
          // THE STRANDING SINK. Reaching here means Firebase Auth has a valid
          // credential but the profile read THREW, so `user` goes null — and
          // every login page keys its redirect on `user`, not on the Firebase
          // credential. The person is therefore authenticated and parked on
          // /login, and before this toast existed they were told NOTHING: the
          // sign-in catch chains suppress their own message for a post-auth
          // failure, Login.tsx's `catch {}` swallows the rethrow, and clicking
          // sign-in again just loops. That silent bounce is the exact defect
          // this file's redirect fix exists to remove, so it must not survive
          // one function downstream of it.
          //
          // SCOPE OF THAT GUARANTEE — read this before making any catch quieter.
          // In the MAIN client this is the only user-visible message on the
          // postAuth path, which is why the postAuth branches in BOTH Google
          // chains can stay silent (the redirect one above, the popup one
          // below): there, the tag decides WHICH message a person gets, not
          // WHETHER they get one.
          //
          // It does NOT hold in client-tech, and the claim is deliberately
          // scoped rather than stated flat. `react-hot-toast` is missing from
          // client-tech/vite.config.ts's `dedupe` list, so that app bundles a
          // SECOND copy: its mounted <Toaster> (client-tech/src/main.tsx) is
          // the only consumer of its own copy, while every toast reachable from
          // @shared dispatches into the other one. A technician therefore sees
          // nothing from this line today.
          //
          // Re-derive rather than trust any of it. These run from ANYWHERE in
          // the repo (the `:/` pathspecs are root-anchored) and each states the
          // value it must produce, so a wrong answer is visible as a wrong
          // answer rather than as silence:
          //
          //   # 1. the dedupe list exists AND lacks react-hot-toast. BOTH halves
          //   #    are asserted on purpose: checking only absence FAILS OPEN --
          //   #    rename the key and the old one-liner printed nothing, which
          //   #    reads exactly like "confirmed absent".
          //   #    The absence check scans the WHOLE FILE, not the matched
          //   #    line: an earlier version piped the `dedupe:` line into a
          //   #    second grep, which reported "absent" whenever the array was
          //   #    reflowed across lines WITH the entry present — measured, not
          //   #    theorised. Whole-file is immune to formatting.
          //   git grep -c "dedupe:" -- ':/client-tech/vite.config.ts'        # must be 1; empty or 0 => THIS COMMENT IS STALE
          //   git grep -c react-hot-toast -- ':/client-tech/vite.config.ts'  # must be empty/0 => not deduped, as documented
          //
          //   # 2. client-tech's own copy has a consumer and NO producer
          //   git grep -n react-hot-toast -- ':/client-tech/src'        # exactly 1 hit: the Toaster import
          //
          //   # 3. BOTH apps declare their own dependency — this is what makes
          //   #    it a SECOND copy rather than a shared one. Deliberately the
          //   #    DECLARATION and not `ls node_modules`: a worktree usually has
          //   #    no client-tech install, so the ls form reports one copy and
          //   #    quietly contradicts this comment in the very environment you
          //   #    are most likely reading it from.
          //   git grep -n '"react-hot-toast"' -- ':/client/package.json' ':/client-tech/package.json'   # must be 2 lines
          //
          //   # 4. UPPER BOUND on the muted surface, not the surface itself:
          //   #    counts FILES importing it, and client-tech reaches only a
          //   #    fraction of them (~30 direct @shared entry points).
          //   git grep -l react-hot-toast -- ':/client/src' | grep -vE '__tests__|\.test\.' | wc -l
          //
          // (#4 is a command, not a typed number: it counts FILES and the value
          //  moves with the tree. An earlier typed version was both stale and
          //  mislabelled "call sites".)
          //
          // THE FIX, and why it is not here: add 'react-hot-toast' to that
          // `dedupe` array. One line — but it un-mutes every @shared toast in
          // the Technician PWA simultaneously, so it needs its own blast-radius
          // measurement and review rather than riding along in an auth change.
          //
          // Two other limits worth knowing: this catch is NOT covered by the
          // `if (!profile)` retry above (that retry only re-reads a MISSING
          // profile; a read that throws gets no second attempt), and it does
          // not sign the user out — so a PERSISTENT failure leaves someone
          // holding a valid credential on /login with NO exit at all. /login
          // offers no sign-out, and a refresh re-enters this same failing read.
          console.error('Error fetching user profile:', error);
          setUser(null);
          setProfileUid(null);
          setProfileFromDevice(false);
          setErrorLoggingUserContext(undefined, undefined);
          toast.error(
            "Signed in, but we couldn't load your profile. Check your connection and refresh to try again.",
            { id: 'profile-stranded' },
          );
        }
      } else {
        // Session ended without an explicit logout() (token expiry, or a
        // sign-out from another tab): drop the remembered inbox filters too, so
        // the next person on this tab does not inherit them (ISS-517). Also
        // covers the "profile missing" branch above, whose firebaseSignOut()
        // re-enters here with a null user. The "profile read threw" branch is
        // deliberately NOT cleared: the credential is still valid and the user
        // is not signed out there.
        // The held just-created tickets belong to the user who is gone; this tab must not show them to the next one.
        clearHeldCreates();
        try {
          clearSessionFilters();
        } catch {
          // A filter-memory convenience must never break the auth flow.
        }
        setUser(null);
        setProfileUid(null);
        setProfileFromDevice(false);
        setErrorLoggingUserContext(undefined, undefined);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // While running on the device's saved profile, re-ask the server as soon as
  // there may be signal (reconnect, or the installed app returning to the
  // foreground — a resume fires no navigation). A success replaces the saved
  // copy; a server REFUSAL drops it (the account was turned away while we were
  // offline); another connectivity failure keeps it and waits for the next event.
  useEffect(() => {
    if (!profileFromDevice || !firebaseUser) return;
    let running = false;
    const refresh = async () => {
      if (running) return;
      running = true;
      try {
        await firebaseUser.getIdToken();
        const profile = await getUserProfile(firebaseUser.uid);
        if (profile) {
          setUser(profile);
          setProfileUid(firebaseUser.uid);
          setProfileFromDevice(false);
          saveIdentity(firebaseUser.uid, { user: profile });
        }
      } catch (error) {
        if (!isConnectivityFailure(error)) {
          console.error('Profile refresh refused after an offline start:', error);
          setUser(null);
          setProfileUid(null);
          setProfileFromDevice(false);
          setErrorLoggingUserContext(undefined, undefined);
        }
      } finally {
        running = false;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [profileFromDevice, firebaseUser]);

  // Login method
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      await firebaseSignIn(email, password);
      toast.success('Successfully logged in');
    } catch (error: any) {
      // A wrong password, a closed popup or a dropped connection is told to the person below and is not a defect (#4630).
      if (isExpectedAuthError(error)) console.warn('Login failed (expected):', error.code);
      else console.error('Login error:', error);

      // Provide user-friendly error messages
      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else if (error.code === 'auth/user-disabled') {
        toast.error('This account has been disabled');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many failed attempts. Please try again later');
      } else {
        toast.error('Failed to log in. Please try again');
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Google Login method
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      const result = await firebaseSignInWithGoogle();
      if (result === null) {
        // Redirect flow — page is navigating away. Reset loading so the button
        // doesn't stay disabled if the user cancels mid-redirect.
        setLoading(false);
        return;
      }
      toast.success('Successfully logged in with Google');
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        console.info('Google sign-in popup closed by user');
        toast.error('Sign-in cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        console.warn('Google sign-in popup blocked:', error);
        toast.error('Popup was blocked. Please allow popups for this site.');
      } else if (error.code === 'auth/user-disabled' || error.message?.includes('user-disabled')) {
        console.warn('Google sign-in: account disabled:', error.message);
        toast.error('This account has been disabled. Please contact your administrator.');
      } else if (error.message?.includes('No account found')) {
        console.warn('Google sign-in: no account found:', error.message);
        toast.error('No account found — please contact your administrator for an invitation.');
      } else if (error.message?.includes('user-disabled') || error.message?.includes('auth/user-disabled')) {
        // Safety net: catch user-disabled that arrives with an unexpected error code
        console.warn('Google sign-in: account disabled (fallthrough):', error.message);
        toast.error('This account has been disabled. Please contact your administrator.');
      } else if (isTransientAuthError(error)) {
        // The network dropped or Google's auth backend answered 5xx mid
        // sign-in (OFFICEHUB-CLIENT-52: signInWithIdp returned 503, surfaced
        // as the unmapped code auth/error-code:-40). Transient and external —
        // warn (not error) so the console bridge doesn't report it as a bug.
        console.warn('Google sign-in: transient network/backend failure:', error.message);
        toast.error("Couldn't reach Google sign-in. Check your connection and try again.");
      } else if (isPostAuthFailure(error)) {
        // The popup ALREADY authenticated this person; ensureUserProfileExists
        // failed afterwards. Telling them "Failed to log in with Google" is a
        // false alarm — onAuthStateChange does its own profile read a moment
        // later and they land on the dashboard. If that read ALSO fails they
        // are told by the stranding sink in the onAuthStateChange catch; this
        // branch is silent because that one speaks. Same defect as the redirect
        // path had, one function away; see auth/postAuthFailure.ts.
        console.error('Google sign-in: profile read failed after sign-in succeeded:', error);
      } else {
        if (isExpectedAuthError(error, 'google')) console.warn('Google login failed (expected):', error.code);
        else console.error('Google login error:', error);
        toast.error('Failed to log in with Google. Please try again');
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout method
  const logout = async () => {
    try {
      await firebaseSignOut();
      analytics.reset();
      // ISS-517: forget the support inbox's remembered filters (never throws).
      clearSessionFilters();
      // A just-created ticket held for display belongs to the signed-out user.
      clearHeldCreates();
      setUser(null);
      setProfileUid(null);
      setFirebaseUser(null);
      // Wipe the on-device Firestore offline cache so a shared field tablet does
      // not retain the prior user's survey/customer data (persistence does not
      // clear on tab close the way the old memory cache did). This terminates
      // the Firestore instance, so hard-reload to /login to reinitialize a
      // fresh, empty one. Runs last so a failure here can't strand sign-out.
      // The last-uid marker is DELIBERATELY LEFT IN PLACE.
      //
      // It used to be removed here, on the reasoning that "this path already wiped".
      // That was true of the Firestore cache and FALSE of locally-autosaved form
      // drafts, which logout does not touch — and removing the marker also disarmed
      // the user-switch guard that would otherwise have cleared them. Net effect:
      // technician A taps Log Out at shift end (the trained action, not an edge
      // case), technician B signs in, `lastUid` is null so the switch-clear is
      // skipped, and A's in-progress report — customer, site, readings, drawn
      // signature — is sitting in B's draft picker.
      //
      // Keeping the marker makes the guard decide, which is the behaviour we
      // actually want in both directions:
      //   same user signs back in  -> uid matches, nothing cleared, THEIR OWN
      //                               drafts survive (local drafts have no
      //                               Firestore copy, so clearing them is
      //                               irreversible work loss);
      //   a different user signs in -> uid differs, drafts and cache both cleared.
      // The only cost is one extra reload when the device genuinely changes hands.
      await clearOfflineCache();
      window.location.assign('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to log out');
      throw error;
    }
  };

  // Send password reset email
  const sendPasswordReset = async (email: string) => {
    try {
      await firebaseResetPassword(email);
      toast.success('Password reset email sent. Check your inbox.');
    } catch (error: any) {
      console.error('Password reset error:', error);

      if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else {
        toast.error('Failed to send password reset email');
      }

      throw error;
    }
  };

  // Update user profile
  const updateProfile = useCallback(async (updates: Partial<User>) => {
    if (!user) {
      throw new Error('No user logged in');
    }

    try {
      await updateUserProfileData(user.userId, updates);

      // Update local state - merge nested objects properly
      setUser((prevUser) => {
        if (!prevUser) return prevUser;
        return {
          ...prevUser,
          ...updates,
          // Merge nested profile object if provided
          profile: updates.profile
            ? { ...prevUser.profile, ...updates.profile }
            : prevUser.profile,
          // Merge nested driverInfo object if provided
          driverInfo: updates.driverInfo
            ? { ...prevUser.driverInfo, ...updates.driverInfo }
            : prevUser.driverInfo,
          // Merge nested preferences object if provided
          preferences: updates.preferences
            ? { ...prevUser.preferences, ...updates.preferences }
            : prevUser.preferences,
          // Merge nested voiceClone object if provided
          voiceClone: updates.voiceClone
            ? { ...prevUser.voiceClone, ...updates.voiceClone }
            : prevUser.voiceClone,
        };
      });
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  }, [user]);

  // Refresh user data from Firestore
  const refreshUser = useCallback(async () => {
    if (!firebaseUser) {
      return;
    }

    try {
      await firebaseUser.getIdToken();
      const profile = await getUserProfile(firebaseUser.uid);
      if (profile) {
        setUser(profile);
        setProfileUid(firebaseUser.uid);
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }
  }, [firebaseUser]);

  // Check if user has a specific role
  const hasRole = useCallback((role: UserRole): boolean => {
    return user?.role === role;
  }, [user?.role]);

  // Check if user is admin (Owner role has implicit admin access)
  const isAdmin = useCallback((): boolean => {
    return user?.role === 'Admin' || user?.role === 'Owner';
  }, [user?.role]);

  // Check if user is manager or admin
  const isManagerOrAdmin = useCallback((): boolean => {
    return user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Owner';
  }, [user?.role]);

  // Check if user is an investor
  const isInvestor = useCallback((): boolean => {
    return user?.role === 'Investor';
  }, [user?.role]);

  const value = useMemo<AuthContextType>(() => ({
    firebaseUser,
    user,
    loading,
    profileFromDevice,
    profileUid,
    login,
    loginWithGoogle,
    logout,
    sendPasswordReset,
    updateProfile,
    refreshUser,
    hasRole,
    isAdmin,
    isManagerOrAdmin,
    isInvestor,
  }), [firebaseUser, user, loading, profileFromDevice, profileUid, updateProfile, refreshUser, hasRole, isAdmin, isManagerOrAdmin, isInvestor]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// HOOK
// ============================================================================

// ============================================================================
// ANONYMOUS PROVIDER — for surfaces framed by another site
// ============================================================================

const refuseInEmbed = (): Promise<never> =>
  Promise.reject(new Error('Signing in is not available inside an embedded form'));

const ANONYMOUS_AUTH: AuthContextType = {
  firebaseUser: null,
  user: null,
  loading: false,
  profileFromDevice: false,
  profileUid: null,
  login: refuseInEmbed,
  loginWithGoogle: refuseInEmbed,
  logout: refuseInEmbed,
  sendPasswordReset: refuseInEmbed,
  updateProfile: refuseInEmbed,
  refreshUser: refuseInEmbed,
  hasRole: () => false,
  isAdmin: () => false,
  isManagerOrAdmin: () => false,
  isInvestor: () => false,
};

/**
 * Supplies a signed-OUT auth context without mounting AuthProvider — no Firebase
 * listener, no profile read, no session. Used ONLY by lib/embed/EmbedOnlyApp.tsx,
 * which renders an embed form inside another site's frame (PR #5628): a staff
 * member's live session must not surface there, so public-form fields that read
 * `useAuth().user` (Signature, EvidenceBundle, DataSourceCombobox) see nobody.
 */
export function AnonymousAuthProvider({ children }: { children: ReactNode }) {
  return <AuthContext.Provider value={ANONYMOUS_AUTH}>{children}</AuthContext.Provider>;
}

/**
 * The auth context, or null outside an AuthProvider. For a component that only READS who is signed in and must still
 * render where no provider is mounted (e.g. a form rendered alone in a test); everything else uses useAuth().
 */
export function useOptionalAuth(): AuthContextType | null {
  return useContext(AuthContext) ?? null;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
